import { salesAnalyticsAxios } from '../config/axiosInstances';

export type ReportAnalyticsEventType = 'OPENED' | 'EXECUTED' | 'EXPORTED' | 'FAILED';
export type AnalyticsPeriod = 'daily' | 'current_month' | 'previous_month';

export interface AnalyticsMetricValues {
  executions: number;
  exports: number;
  errors: number;
}

export interface AnalyticsReportUsageItem {
  date_key?: string;
  metric_key?: string;
  pk?: string;
  sk?: string;
  date: string;
  report_type?: string;
  metrics: AnalyticsMetricValues;
}

export interface AnalyticsUserActivityItem {
  date_key?: string;
  metric_key?: string;
  pk?: string;
  sk?: string;
  date: string;
  user_id?: string;
  metrics: AnalyticsMetricValues;
}

export interface AnalyticsDimensionActivityItem {
  date_key?: string;
  metric_key?: string;
  pk?: string;
  sk?: string;
  date: string;
  country?: string;
  role?: string;
  report_type?: string;
  metrics: AnalyticsMetricValues;
}

export interface AnalyticsCountryUsersItem {
  date_key?: string;
  metric_key?: string;
  pk?: string;
  sk?: string;
  date?: string;
  country?: string;
  users?: number | string;
  total_users?: number | string;
  metrics?: {
    users?: number | string;
    total_users?: number | string;
    unique_users?: number | string;
  };
}

export interface AnalyticsSummary {
  executions: number;
  exports: number;
  errors: number;
  users: number;
  countries: number;
  roles: number;
}

export interface AnalyticsSummaryApiItem {
  metrics?: {
    unique_roles?: string | number;
    unique_users?: string | number;
    unique_country?: string | number;
    total_executions?: string | number;
    total_exports?: string | number;
    errors?: string | number;
  };
}

export interface TrackReportEventRequest {
  report_id?: string;
  report_type: string;
  event_type: ReportAnalyticsEventType;
  timestamp?: string;
  filters?: unknown;
  duration?: number;
  domain?: 'sales' | 'collections';
  user_id?: string;
  country?: string;
  role?: string;
  roles?: string[];
}

export default class {
  static async trackReportEvent(request: TrackReportEventRequest) {
    return salesAnalyticsAxios.post<{ accepted: number }>('/analytics', request);
  }

  static async findSummary(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsSummaryApiItem[]>('/analytics/summary', {
      params: { date, period: selectedPeriod },
    });
  }

  static async findReportsUsage(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsReportUsageItem[]>('/analytics/reports/usage', {
      params: { date, period: selectedPeriod },
    });
  }

  static async findReportsTop(period?: AnalyticsPeriod, date?: string, limit?: number) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsReportUsageItem[]>('/analytics/reports/top', {
      params: { limit, date, period: selectedPeriod },
    });
  }

  static async findUsersActivity(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsUserActivityItem[]>('/analytics/users/activity', {
      params: { date, period: selectedPeriod },
    });
  }

  static async findCountriesActivity(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsDimensionActivityItem[]>(
      '/analytics/countries/activity',
      {
        params: { date, period: selectedPeriod },
      }
    );
  }

  static async findRolesActivity(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsDimensionActivityItem[]>('/analytics/roles/activity', {
      params: { date, period: selectedPeriod },
    });
  }

  static async findCountryReportsActivity(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsDimensionActivityItem[]>(
      '/analytics/countries/reports',
      {
        params: { date, period: selectedPeriod },
      }
    );
  }

  static async findRoleReportsActivity(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsDimensionActivityItem[]>('/analytics/roles/reports', {
      params: { date, period: selectedPeriod },
    });
  }

  static async findCountryRolesActivity(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsDimensionActivityItem[]>('/analytics/countries/roles', {
      params: { date, period: selectedPeriod },
    });
  }

  static async findCountriesUsers(period?: AnalyticsPeriod, date?: string) {
    const selectedPeriod = period || 'daily';
    return salesAnalyticsAxios.get<AnalyticsCountryUsersItem[]>('/analytics/countries/users', {
      params: { date, period: selectedPeriod },
    });
  }
}
